import React, { useState, useEffect } from 'react';
import { RefreshCw, Database, FileSpreadsheet, Users, Clock, CheckCircle, XCircle, AlertCircle, Activity, HardDrive, Server, Timer, Zap, Download, Trash2, FolderOpen, Plus, Edit, Key, UserPlus, X, Eye, EyeOff } from 'lucide-react';
import Toast from '../components/Toast';
import { ROLE_OPTIONS, DEFAULT_ROLE, formatRole } from '../roles';

function formatDuration(seconds) {
  if (!seconds) return '-';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatNumber(n) {
  return (n || 0).toLocaleString();
}

// Manual (on-demand) sync sources - these have a "Sync Now" button.
// The scheduled sources live in autoSyncConfig and are switched on/off there.
const syncConfig = {
  erp_invoices: { label: 'Posted Sales Invoices', icon: FileSpreadsheet, color: '#3b82f6', description: 'Sync invoices from Business Central ERP' },
  excel_onedrive: { label: 'Excel (OneDrive)', icon: FileSpreadsheet, color: '#10b981', description: 'Sync delivery records from OneDrive Excel file' },
  erp_customers: { label: 'Customers', icon: Users, color: '#8b5cf6', description: 'Sync customer data from Business Central ERP' },
  manual_backup: { label: 'Manual Backup', icon: Database, color: '#f59e0b', description: 'Manual database backup' },
};

// Scheduled sync sources - they run on their own every N hours and can be
// switched on/off. There is no "Sync Now" button for these.
const autoSyncConfig = {
  auto_erp_invoices: { label: 'Posted Sales Invoices', icon: FileSpreadsheet, color: '#3b82f6', description: 'Auto-sync invoices from BC (all companies)' },
  auto_erp_customers: { label: 'Customers', icon: Users, color: '#8b5cf6', description: 'Auto-sync customers from BC (all companies)' },
  auto_excel_onedrive: { label: 'Excel (OneDrive)', icon: FileSpreadsheet, color: '#10b981', description: 'Auto-sync deliveries from OneDrive Excel' },
};

export default function Settings() {
  const [syncStatus, setSyncStatus] = useState({});
  const [autoSyncStatus, setAutoSyncStatus] = useState({});
  const [autoSyncSources, setAutoSyncSources] = useState({});
  const [autoSyncSchedule, setAutoSyncSchedule] = useState({ intervalHours: 6, nextRunAt: null });
  const [toggling, setToggling] = useState(null);
  const [recordCounts, setRecordCounts] = useState({});
  const [syncHistory, setSyncHistory] = useState([]);
  const [systemInfo, setSystemInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [syncProgress, setSyncProgress] = useState('');
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [backups, setBackups] = useState([]);
  const [backingUp, setBackingUp] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ company: '', username: '', password: '', full_name: '', role: DEFAULT_ROLE, is_active: true });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ userId: null, newPassword: '' });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  async function loadData() {
    setLoading(true);
    try {
      const [statusRes, historyRes, systemRes, backupsRes, usersRes, autoSyncRes] = await Promise.all([
        fetch('/api/settings/sync-status', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/settings/sync-history?limit=10', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/settings/system-info', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/backup/list', { credentials: 'include' }).then(r => r.json()).catch(() => ({ data: [] })),
        fetch('/api/users', { credentials: 'include' }).then(r => r.json()).catch(() => ({ data: [] })),
        fetch('/api/settings/auto-sync', { credentials: 'include' }).then(r => r.json()).catch(() => null),
      ]);
      const allStatus = statusRes.syncStatus || {};

      // Separate manual and auto sync status
      const manual = {};
      const auto = {};
      for (const [key, value] of Object.entries(allStatus)) {
        if (key.startsWith('auto_')) {
          auto[key] = value;
        } else {
          manual[key] = value;
        }
      }

      setSyncStatus(manual);
      setAutoSyncStatus(auto);
      setRecordCounts(statusRes.recordCounts || {});
      setSyncHistory(historyRes.data || []);
      setSystemInfo(systemRes);
      setBackups(backupsRes.data || []);
      setUsersList(usersRes.data || []);

      if (autoSyncRes && autoSyncRes.sources) {
        setAutoSyncSources(autoSyncRes.sources);
        setAutoSyncSchedule({
          intervalHours: autoSyncRes.intervalHours || 6,
          nextRunAt: autoSyncRes.nextRunAt || null
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
    setLoading(false);
  }

  useEffect(() => { loadData(); }, []);

  // Switches a scheduled sync on or off. The choice is stored on the server
  // so it survives a restart of the backend.
  async function toggleAutoSync(source, enabled) {
    setToggling(source);
    try {
      const res = await fetch(`/api/settings/auto-sync/${source}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled })
      });

      const data = await res.json();
      if (!res.ok) {
        setToast({ message: data.error || 'Failed to update auto-sync', type: 'error', duration: 5000 });
        return;
      }

      if (data.sources) setAutoSyncSources(data.sources);
      setToast({
        message: `Auto-sync ${data.enabled ? 'enabled' : 'disabled'} for ${data.label || source}`,
        type: 'success',
        duration: 3000
      });
    } catch (err) {
      setToast({ message: err.message, type: 'error', duration: 5000 });
    } finally {
      setToggling(null);
    }
  }

  async function createBackup() {
    setBackingUp(true);
    try {
      const res = await fetch('/api/backup/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prefix: 'manual' })
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ message: data.message, type: 'success', duration: 5000 });
        loadData();
      } else {
        setToast({ message: data.error || 'Backup failed', type: 'error', duration: 5000 });
      }
    } catch (err) {
      setToast({ message: err.message, type: 'error', duration: 5000 });
    } finally {
      setBackingUp(false);
    }
  }

  async function deleteBackup(filename) {
    if (!window.confirm(`Delete backup "${filename}"?`)) return;
    try {
      const res = await fetch(`/api/backup/${filename}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setToast({ message: 'Backup deleted', type: 'success', duration: 3000 });
        loadData();
      } else {
        const data = await res.json();
        setToast({ message: data.error || 'Failed to delete', type: 'error', duration: 3000 });
      }
    } catch (err) {
      setToast({ message: err.message, type: 'error', duration: 3000 });
    }
  }

  function openAddUserModal() {
    setEditingUser(null);
    setUserForm({ company: '', username: '', password: '', full_name: '', role: DEFAULT_ROLE, is_active: true });
    setShowUserModal(true);
  }

  function openEditUserModal(user) {
    setEditingUser(user);
    setUserForm({ 
      company: user.company, 
      username: user.username, 
      password: '', 
      full_name: user.full_name || '', 
      role: user.role, 
      is_active: user.is_active 
    });
    setShowUserModal(true);
  }

  function closeUserModal() {
    setShowUserModal(false);
    setEditingUser(null);
    setUserForm({ company: '', username: '', password: '', full_name: '', role: DEFAULT_ROLE, is_active: true });
  }

  async function saveUser() {
    if (!userForm.company || !userForm.username) {
      setToast({ message: 'Company and username are required', type: 'error', duration: 3000 });
      return;
    }
    if (!editingUser && !userForm.password) {
      setToast({ message: 'Password is required for new users', type: 'error', duration: 3000 });
      return;
    }

    setSavingUser(true);
    try {
      const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
      const method = editingUser ? 'PUT' : 'POST';
      
      const body = { ...userForm };
      if (editingUser && !body.password) {
        delete body.password;
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (res.ok) {
        setToast({ message: data.message || 'User saved', type: 'success', duration: 3000 });
        closeUserModal();
        loadData();
      } else {
        setToast({ message: data.error || 'Failed to save user', type: 'error', duration: 3000 });
      }
    } catch (err) {
      setToast({ message: err.message, type: 'error', duration: 3000 });
    } finally {
      setSavingUser(false);
    }
  }

  async function deleteUser(userId) {
    if (!window.confirm('Delete this user?')) return;
    try {
      const res = await fetch(`/api/users/${userId}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        setToast({ message: data.message || 'User deleted', type: 'success', duration: 3000 });
        loadData();
      } else {
        setToast({ message: data.error || 'Failed to delete user', type: 'error', duration: 3000 });
      }
    } catch (err) {
      setToast({ message: err.message, type: 'error', duration: 3000 });
    }
  }

  function openPasswordModal(userId) {
    setPasswordForm({ userId, newPassword: '' });
    setShowResetPassword(false);
    setShowPasswordModal(true);
  }

  async function resetPassword() {
    if (!passwordForm.newPassword) {
      setToast({ message: 'New password is required', type: 'error', duration: 3000 });
      return;
    }
    try {
      const res = await fetch(`/api/users/${passwordForm.userId}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ newPassword: passwordForm.newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ message: data.message || 'Password reset', type: 'success', duration: 3000 });
        setShowPasswordModal(false);
        setPasswordForm({ userId: null, newPassword: '' });
      } else {
        setToast({ message: data.error || 'Failed to reset password', type: 'error', duration: 3000 });
      }
    } catch (err) {
      setToast({ message: err.message, type: 'error', duration: 3000 });
    }
  }

  async function triggerSync(type) {
    setSyncing(type);
    setSyncProgress('Starting sync...');
    const config = syncConfig[type];

    try {
      let url, body;
      if (type === 'erp_invoices') {
        url = '/api/bc/sync-invoices';
        body = JSON.stringify({ company: 'YSU Marketing Corporation LIVE' });
      } else if (type === 'excel_onedrive') {
        url = '/api/deliveries/sync-from-excel';
        body = null;
      } else if (type === 'erp_customers') {
        url = '/api/bc/sync-customers';
        body = JSON.stringify({ company: 'YSU Marketing Corporation LIVE' });
      }

      setSyncProgress(`Connecting to ${config.label}...`);

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body,
      });

      const data = await res.json();

      if (res.ok) {
        setSyncProgress('Sync completed successfully!');
        setToast({ message: data.message, type: 'success', duration: 5000 });
        loadData();
      } else {
        setSyncProgress('Sync failed!');
        setToast({ message: data.error || 'Sync failed', type: 'error', duration: 5000 });
      }
    } catch (err) {
      setSyncProgress('Sync failed!');
      setToast({ message: err.message, type: 'error', duration: 5000 });
    } finally {
      setTimeout(() => setSyncing(null), 1500);
    }
  }

  function getStatusIcon(status) {
    if (status === 'completed') return <CheckCircle size={16} style={{ color: '#10b981' }} />;
    if (status === 'failed') return <XCircle size={16} style={{ color: '#ef4444' }} />;
    return <AlertCircle size={16} style={{ color: '#f59e0b' }} />;
  }

  function getStatusBadge(status) {
    const styles = {
      completed: { background: '#dcfce7', color: '#166534' },
      failed: { background: '#fee2e2', color: '#991b1b' },
      running: { background: '#dbeafe', color: '#1e40af' },
    };
    const s = styles[status] || styles.running;
    return (
      <span style={{ ...s, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
        {status}
      </span>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <RefreshCw size={24} className="spin" /> <span style={{ marginLeft: 8 }}>Loading settings...</span>
      </div>
    );
  }

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={() => setToast(null)} />}

      {/* Sync Progress Modal */}
      {syncing && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white', borderRadius: 16, padding: 32, maxWidth: 400, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center'
          }}>
            <div style={{
              width: 48, height: 48, border: '4px solid #e2e8f0', borderTopColor: syncConfig[syncing]?.color || '#3b82f6',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
            }} />
            <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{syncConfig[syncing]?.label || 'Syncing'}...</h3>
            <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>{syncProgress}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 24 }}>
        {[
          { id: 'overview', label: 'Overview', icon: Activity },
          { id: 'history', label: 'Sync History', icon: Clock },
          { id: 'backup', label: 'Backup', icon: FolderOpen },
          { id: 'users', label: 'Users', icon: Users },
          { id: 'system', label: 'System Info', icon: Server },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 20px', border: 'none', borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? '#3b82f6' : '#64748b', marginBottom: -2, fontFamily: 'inherit'
            }}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Manual (on-demand) runs */}
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <RefreshCw size={18} style={{ color: '#3b82f6' }} /> Manual Sync
            <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
              Optional — these sources already refresh automatically every {autoSyncSchedule.intervalHours} hour{autoSyncSchedule.intervalHours === 1 ? '' : 's'}
            </span>
          </h3>
          {/* Sync Status Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 24 }}>
            {Object.entries(syncConfig).map(([key, config]) => {
              const status = syncStatus[key];
              const Icon = config.icon;
              return (
                <div key={key} style={{
                  background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
                  padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: `${config.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <Icon size={20} style={{ color: config.color }} />
                      </div>
                      <div>
                        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{config.label}</h4>
                        <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>{config.description}</p>
                      </div>
                    </div>
                    {status && getStatusBadge(status.status)}
                  </div>

                  {status && (
                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                      {status.status === 'completed' && (
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          {status.records_inserted > 0 && <span>Inserted: <strong>{formatNumber(status.records_inserted)}</strong></span>}
                          {status.records_updated > 0 && <span>Updated: <strong>{formatNumber(status.records_updated)}</strong></span>}
                          {status.records_skipped > 0 && <span>Skipped: <strong>{formatNumber(status.records_skipped)}</strong></span>}
                          {status.duration_seconds && <span>Duration: <strong>{formatDuration(status.duration_seconds)}</strong></span>}
                        </div>
                      )}
                      <div style={{ marginTop: 6, fontSize: 12 }}>
                        Last sync: {formatDate(status.completed_at || status.started_at)}
                      </div>
                    </div>
                  )}

                  {!status && (
                    <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 12px' }}>No sync history yet</p>
                  )}

                  <button
                    onClick={() => (key === 'manual_backup' ? createBackup() : triggerSync(key))}
                    disabled={syncing !== null || (key === 'manual_backup' && backingUp)}
                    style={{
                      width: '100%', padding: '8px 16px', borderRadius: 8, border: 'none',
                      background: syncing === key ? '#94a3b8' : config.color,
                      color: 'white', fontWeight: 600, fontSize: 13, cursor: syncing === key ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'inherit'
                    }}
                  >
                    <RefreshCw size={14} className={(syncing === key || (key === 'manual_backup' && backingUp)) ? 'spin' : ''} />
                    {key === 'manual_backup'
                      ? (backingUp ? 'Backing up...' : 'Backup Now')
                      : (syncing === key ? 'Syncing...' : 'Sync Now')}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Auto-Sync Status */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Zap size={18} style={{ color: '#f59e0b' }} /> Auto-Sync Status
              <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
                Runs automatically every {autoSyncSchedule.intervalHours} hour{autoSyncSchedule.intervalHours === 1 ? '' : 's'}
                {autoSyncSchedule.nextRunAt && ` — next run ${formatDate(autoSyncSchedule.nextRunAt)}`}
              </span>
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#94a3b8' }}>
              No "Sync Now" needed — these run on their own. Use the switch to pause or resume a source.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
              {Object.entries(autoSyncConfig).map(([key, config]) => {
                const status = autoSyncStatus[key];
                const Icon = config.icon;
                const isEnabled = autoSyncSources[key] ? autoSyncSources[key].enabled !== false : true;
                const isBusy = toggling === key;
                return (
                  <div key={key} style={{
                    background: '#f8fafc', borderRadius: 10, padding: 16,
                    border: '1px solid #e2e8f0',
                    opacity: isEnabled ? 1 : 0.65
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: `${config.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <Icon size={16} style={{ color: config.color }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{config.label}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{config.description}</div>
                        </div>
                      </div>
                      {isEnabled
                        ? (status && getStatusBadge(status.status))
                        : <span style={{ background: '#f1f5f9', color: '#64748b', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>Disabled</span>}
                    </div>
                    {status ? (
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {status.status === 'completed' && (
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                            {status.records_inserted > 0 && <span>Inserted: <strong>{formatNumber(status.records_inserted)}</strong></span>}
                            {status.records_updated > 0 && <span>Updated: <strong>{formatNumber(status.records_updated)}</strong></span>}
                            {status.records_skipped > 0 && <span>Skipped: <strong>{formatNumber(status.records_skipped)}</strong></span>}
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                          <Clock size={10} />
                          Last run: {formatDate(status.completed_at || status.started_at)}
                          {status.duration_seconds && <span style={{ marginLeft: 8 }}>({formatDuration(status.duration_seconds)})</span>}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>No auto-sync history yet</div>
                    )}
                    <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        {isEnabled ? 'Auto-sync is running' : 'Auto-sync is paused'}
                      </span>
                      <button
                        onClick={() => toggleAutoSync(key, !isEnabled)}
                        disabled={isBusy}
                        aria-pressed={isEnabled}
                        title={isEnabled ? 'Disable this auto-sync' : 'Enable this auto-sync'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 8,
                          padding: '5px 12px', borderRadius: 20, cursor: isBusy ? 'wait' : 'pointer',
                          border: `1px solid ${isEnabled ? '#10b981' : '#cbd5e1'}`,
                          background: isEnabled ? '#dcfce7' : '#f1f5f9',
                          color: isEnabled ? '#166534' : '#64748b',
                          fontSize: 12, fontWeight: 600, fontFamily: 'inherit'
                        }}
                      >
                        <span style={{
                          width: 28, height: 16, borderRadius: 8, display: 'inline-block',
                          background: isEnabled ? '#10b981' : '#cbd5e1', position: 'relative',
                          transition: 'background 0.15s'
                        }}>
                          <span style={{
                            position: 'absolute', top: 2, left: isEnabled ? 14 : 2, width: 12, height: 12,
                            borderRadius: 6, background: '#fff', transition: 'left 0.15s'
                          }} />
                        </span>
                        {isBusy ? 'Saving...' : (isEnabled ? 'Enabled' : 'Disabled')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Record Counts */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Database size={18} /> Record Counts
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              {[
                { label: 'Deliveries', count: recordCounts.deliveries, color: '#3b82f6' },
                { label: 'Posted Sales Invoices', count: recordCounts.posted_sales_invoices, color: '#10b981' },
                { label: 'Customers', count: recordCounts.customers, color: '#8b5cf6' },
              ].map(item => (
                <div key={item.label} style={{
                  background: `${item.color}08`, borderRadius: 10, padding: 16, textAlign: 'center',
                  border: `1px solid ${item.color}20`
                }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{formatNumber(item.count)}</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sync History Tab */}
      {activeTab === 'history' && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={18} /> Recent Sync History
            </h3>
          </div>
          {syncHistory.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No sync history yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Inserted</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Updated</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Skipped</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Duration</th>
                  <th style={thStyle}>Completed</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.map((log, i) => {
                  const config = syncConfig[log.sync_type] || { label: log.sync_type, color: '#64748b' };
                  return (
                    <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600, color: config.color }}>{config.label}</span>
                      </td>
                      <td style={tdStyle}>{getStatusBadge(log.status)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(log.records_inserted)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(log.records_updated)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(log.records_skipped)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatDuration(log.duration_seconds)}</td>
                      <td style={{ ...tdStyle, fontSize: 12 }}>{formatDate(log.completed_at || log.started_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          {/* Add User Button */}
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={openAddUserModal}
              style={{
                padding: '10px 20px', borderRadius: 8, border: 'none',
                background: '#3b82f6', color: 'white', fontWeight: 600, fontSize: 14,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit'
              }}
            >
              <UserPlus size={18} /> Add New User
            </button>
          </div>

          {/* Users Table */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={18} /> System Users ({usersList.length})
              </h3>
            </div>
            {usersList.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No users yet. Click "Add New User" to create one.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={thStyle}>Company</th>
                    <th style={thStyle}>Username</th>
                    <th style={thStyle}>Full Name</th>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Created</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {usersList.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600, color: '#3b82f6' }}>{user.company}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{user.username}</span>
                      </td>
                      <td style={tdStyle}>{user.full_name || '-'}</td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                          background: user.role === 'admin' ? '#dbeafe' : '#f1f5f9',
                          color: user.role === 'admin' ? '#1e40af' : '#64748b',
                          textTransform: 'capitalize'
                        }}>
                          {formatRole(user.role)}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                          background: user.is_active ? '#dcfce7' : '#fee2e2',
                          color: user.is_active ? '#166534' : '#991b1b'
                        }}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12 }}>{formatDate(user.created_at)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => openEditUserModal(user)}
                            style={{
                              background: 'none', border: 'none', color: '#3b82f6',
                              cursor: 'pointer', padding: '4px 8px', borderRadius: 4
                            }}
                            title="Edit user"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => openPasswordModal(user.id)}
                            style={{
                              background: 'none', border: 'none', color: '#f59e0b',
                              cursor: 'pointer', padding: '4px 8px', borderRadius: 4
                            }}
                            title="Reset password"
                          >
                            <Key size={14} />
                          </button>
                          <button
                            onClick={() => deleteUser(user.id)}
                            style={{
                              background: 'none', border: 'none', color: '#ef4444',
                              cursor: 'pointer', padding: '4px 8px', borderRadius: 4
                            }}
                            title="Delete user"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white', borderRadius: 16, padding: 32, maxWidth: 500, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              <button
                onClick={closeUserModal}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  color: '#64748b', borderRadius: 4
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                  Company <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={userForm.company}
                  onChange={(e) => setUserForm({ ...userForm, company: e.target.value })}
                  placeholder="e.g., YSU Marketing Corporation LIVE"
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                    fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                  Username <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  placeholder="e.g., jdoe"
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                    fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                  Password {!editingUser && <span style={{ color: '#ef4444' }}>*</span>}
                  {editingUser && <span style={{ fontWeight: 400, color: '#94a3b8' }}> (leave blank to keep current)</span>}
                </label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                    fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                  placeholder="e.g., John Doe"
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                    fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                  Role
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                    fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                    background: 'white', cursor: 'pointer'
                  }}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="is_active"
                  checked={userForm.is_active}
                  onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="is_active" style={{ fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                  Active
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                onClick={closeUserModal}
                style={{
                  flex: 1, padding: '10px 20px', borderRadius: 8, border: '1px solid #cbd5e1',
                  background: 'white', color: '#64748b', fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveUser}
                disabled={savingUser}
                style={{
                  flex: 1, padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: savingUser ? '#94a3b8' : '#3b82f6', color: 'white', fontWeight: 600, fontSize: 14,
                  cursor: savingUser ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                }}
              >
                <RefreshCw size={14} className={savingUser ? 'spin' : ''} />
                {savingUser ? 'Saving...' : 'Save User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white', borderRadius: 16, padding: 32, maxWidth: 400, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Key size={20} style={{ color: '#f59e0b' }} /> Reset Password
              </h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  color: '#64748b', borderRadius: 4
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                New Password <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showResetPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password"
                  style={{
                    width: '100%', padding: '10px 40px 10px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                    fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                    color: '#94a3b8', display: 'flex', alignItems: 'center'
                  }}
                  tabIndex={-1}
                >
                  {showResetPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{
                  flex: 1, padding: '10px 20px', borderRadius: 8, border: '1px solid #cbd5e1',
                  background: 'white', color: '#64748b', fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Cancel
              </button>
              <button
                onClick={resetPassword}
                style={{
                  flex: 1, padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: '#f59e0b', color: 'white', fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* System Info Tab */}
      {activeTab === 'system' && systemInfo && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
              <HardDrive size={16} /> Database
            </h4>
            <div style={{ fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>Name</span>
                <span style={{ fontWeight: 600 }}>{systemInfo.database.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ color: '#64748b' }}>Size</span>
                <span style={{ fontWeight: 600 }}>{systemInfo.database.size_mb} MB</span>
              </div>
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Server size={16} /> Service
            </h4>
            <div style={{ fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>Node Version</span>
                <span style={{ fontWeight: 600 }}>{systemInfo.nodeVersion}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b' }}>Environment</span>
                <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{systemInfo.environment}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ color: '#64748b' }}>Uptime</span>
                <span style={{ fontWeight: 600 }}>{formatDuration(Math.round(systemInfo.uptime))}</span>
              </div>
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Database size={16} /> Record Counts
            </h4>
            <div style={{ fontSize: 13 }}>
              {Object.entries(systemInfo.recordCounts || {}).map(([key, count]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                  <span style={{ fontWeight: 600 }}>{formatNumber(count)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Backup Tab */}
      {activeTab === 'backup' && (
        <div>
          {/* Backup Actions */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Database size={18} /> Database Backup
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
              <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 16, border: '1px solid #bbf7d0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Zap size={16} style={{ color: '#10b981' }} />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Automatic Backup</span>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
                  Runs daily at 2:00 AM Philippine time. Backups are saved to the server and the last 14 daily copies are kept.
                </p>
                <div style={{ fontSize: 12, color: '#10b981', fontWeight: 500 }}>
                  <CheckCircle size={12} style={{ display: 'inline', marginRight: 4 }} />
                  Enabled
                </div>
              </div>
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: 16, border: '1px solid #bfdbfe' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Download size={16} style={{ color: '#3b82f6' }} />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Manual Backup</span>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
                  Create a backup on demand. Useful before major changes.
                </p>
                <button
                  onClick={createBackup}
                  disabled={backingUp}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: backingUp ? '#94a3b8' : '#3b82f6',
                    color: 'white', fontWeight: 600, fontSize: 13, cursor: backingUp ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6
                  }}
                >
                  <RefreshCw size={14} className={backingUp ? 'spin' : ''} />
                  {backingUp ? 'Creating...' : 'Create Backup Now'}
                </button>
              </div>
            </div>
          </div>

          {/* Backup List */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FolderOpen size={18} /> Backup Files ({backups.length})
              </h3>
            </div>
            {backups.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No backups yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={thStyle}>Filename</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Size</th>
                    <th style={thStyle}>Created</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={tdStyle}>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#3b82f6' }}>{backup.filename}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{backup.sizeMB} MB</td>
                      <td style={{ ...tdStyle, fontSize: 12 }}>{formatDate(backup.createdAt)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <button
                          onClick={() => deleteBackup(backup.filename)}
                          style={{
                            background: 'none', border: 'none', color: '#ef4444',
                            cursor: 'pointer', padding: '4px 8px', borderRadius: 4
                          }}
                          title="Delete backup"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e2e8f0'
};

const tdStyle = {
  padding: '10px 16px', fontSize: 13, color: '#334155'
};
